import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import {
  AdminUserBreadcrumbActivityDto,
  AdminUserBreadcrumbActivityType,
  ModerationContentType,
} from '@ivy-monorepo/shared'
import { BaseModalProps } from 'src/types/modal'
import PropertyTag from '../PropertyTag'
import BreadcrumbActivityBox from '../BreadcrumbActivityBox'
import { adminService } from 'src/services/admin.service'

interface UserBreadcrumbActivityModalProps extends BaseModalProps {
  activity: AdminUserBreadcrumbActivityDto | null
  userName?: string
  onModerationChange?: () => void
  onDelete: (publicId: string) => Promise<void>
  onDeleted?: (publicId: string) => void
}

const UserBreadcrumbActivityModal = ({
  isOpen,
  onClose,
  activity,
  userName,
  onModerationChange,
  onDelete,
  onDeleted,
}: UserBreadcrumbActivityModalProps) => {
  const [breadcrumbFlagged, setBreadcrumbFlagged] = useState(false)
  const [replyFlagged, setReplyFlagged] = useState(false)

  useEffect(() => {
    if (!activity) return

    if (activity.type === AdminUserBreadcrumbActivityType.Reply) {
      setBreadcrumbFlagged(!!activity.parentBreadcrumb?.isFlagged)
      setReplyFlagged(!!activity.isFlagged)
      return
    }

    setBreadcrumbFlagged(!!activity.isFlagged)
    setReplyFlagged(false)
  }, [activity])

  if (!activity) {
    return null
  }

  const isReply = activity.type === AdminUserBreadcrumbActivityType.Reply
  const replySectionTitle = userName ? `${userName}'s reply` : "This user's reply"

  const handleModerationChange = () => {
    onModerationChange?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className={`modal-popup confirm-popup${breadcrumbFlagged ? ' moderation-flagged-content' : ''}`}
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onAfterClose={() => {
        document.body.classList.remove('ReactModal__Body--open')
      }}
    >
      <div className="modal-content-wrapper">
        <div className="moderation-modal-header">
          <h2>{isReply ? 'Breadcrumb reply' : 'Breadcrumb'}</h2>
          <PropertyTag value={activity.type} />
          <div className="moderation-modal-header-flags">
            {breadcrumbFlagged && <span className="moderation-flag-badge">Breadcrumb flagged</span>}
            {isReply && replyFlagged && (
              <span className="moderation-flag-badge moderation-flag-badge--reply">Reply flagged</span>
            )}
          </div>
        </div>

        <div className="breadcrumb-activity-sections">
          {isReply && activity.parentBreadcrumb && activity.author ? (
            <>
              <BreadcrumbActivityBox
                title="Breadcrumb"
                variant="breadcrumb"
                content={activity.parentBreadcrumb.content}
                reactions={activity.parentBreadcrumb.reactions}
                meta={[
                  {
                    label: 'Author',
                    value: `${activity.parentBreadcrumb.author.firstName} ${activity.parentBreadcrumb.author.lastName}`,
                  },
                  { label: 'Venue', value: activity.venue.name },
                  { label: 'Posted', value: activity.parentBreadcrumb.createdAt, type: 'datetime' },
                ]}
                moderation={{
                  authorPublicId: activity.parentBreadcrumb.author.publicId,
                  contentType: ModerationContentType.VenueBreadcrumb,
                  contentReferenceId: activity.parentBreadcrumb.publicId,
                  quotedContent: activity.parentBreadcrumb.content,
                  isFlagged: breadcrumbFlagged,
                  onFlagChange: flagged => {
                    setBreadcrumbFlagged(flagged)
                    handleModerationChange()
                  },
                  onDelete: async () => {
                    await onDelete(activity.parentBreadcrumb!.publicId)
                    onDeleted?.(activity.parentBreadcrumb!.publicId)
                  },
                  deleteConfirmTitle: 'Delete breadcrumb?',
                  deleteConfirmBody:
                    'This will hide the original breadcrumb and its replies from users. Content is retained for audit.',
                }}
              />
              <BreadcrumbActivityBox
                title={replySectionTitle}
                variant="reply"
                content={activity.content}
                reactions={activity.reactions}
                meta={[
                  { label: 'Venue', value: activity.venue.name },
                  { label: 'Replied', value: activity.createdAt, type: 'datetime' },
                ]}
                moderation={{
                  authorPublicId: activity.author.publicId,
                  contentType: ModerationContentType.VenueBreadcrumbReply,
                  contentReferenceId: activity.publicId,
                  quotedContent: activity.content,
                  isFlagged: replyFlagged,
                  onFlagChange: flagged => {
                    setReplyFlagged(flagged)
                    handleModerationChange()
                  },
                  onDelete: async () => {
                    await adminService.deleteVenueBreadcrumbReply(activity.publicId)
                    onDeleted?.(activity.publicId)
                  },
                  deleteConfirmTitle: 'Delete reply?',
                  deleteConfirmBody: 'This will hide this reply from users. Content is retained for audit.',
                }}
              />
            </>
          ) : (
            <BreadcrumbActivityBox
              title="Breadcrumb"
              variant="breadcrumb"
              content={activity.content}
              reactions={activity.reactions}
              meta={[
                { label: 'Venue', value: activity.venue.name },
                { label: 'Posted', value: activity.createdAt, type: 'datetime' },
              ]}
              moderation={
                activity.author
                  ? {
                      authorPublicId: activity.author.publicId,
                      contentType: ModerationContentType.VenueBreadcrumb,
                      contentReferenceId: activity.publicId,
                      quotedContent: activity.content,
                      isFlagged: breadcrumbFlagged,
                      onFlagChange: flagged => {
                        setBreadcrumbFlagged(flagged)
                        handleModerationChange()
                      },
                      onDelete: async () => {
                        await onDelete(activity.publicId)
                        onDeleted?.(activity.publicId)
                      },
                      deleteConfirmTitle: 'Delete breadcrumb?',
                      deleteConfirmBody: 'This will hide the breadcrumb from users. It is retained for audit.',
                    }
                  : undefined
              }
            />
          )}
        </div>

        <div className="modal-footer-content">
          <button className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default UserBreadcrumbActivityModal
